"use client";
import { motion } from "framer-motion";
import { FileText, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

export default function TermsAndConditionsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto px-4 py-8"
      >
        <div className="flex items-center justify-between mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6"
          >
            <FileText className="h-8 w-8 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            Terms and Conditions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            Terms of service for using AssignOwl platform
          </motion.p>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-white/[0.1] space-y-8">

            {/* Acceptance */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Acceptance of Terms</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  By accessing and using AssignOwl ("Service"), provided by Greenguide Solutions Pvt Limited ("Company", "we", "us", or "our"), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our Service.
                </p>
              </div>
            </section>

            {/* Service Description */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Service Description</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  AssignOwl is an AI-powered assignment scoring platform that provides:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Automated grading and scoring of academic assignments</li>
                  <li>Detailed feedback based on custom or predefined rubrics</li>
                  <li>Assignment history and performance tracking</li>
                  <li>Downloadable score reports and analytics</li>
                </ul>
              </div>
            </section>

            {/* Payments & Credits */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Payments & Credits</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Credit-Based System:</strong> AssignOwl operates on a credit-based payment model. Credits must be purchased to use assignment grading services.
                </p>
                <p>
                  <strong className="text-foreground">Pricing:</strong> Current pricing is $5 for 40 credits and $20 for 260 credits. 20 credits allow you to grade one complete assignment.
                </p>
                <p>
                  <strong className="text-foreground">Credit Usage:</strong> Credits are consumed when you successfully submit an assignment for grading. 20 credits equal one assignment evaluation.
                </p>
                <p>
                  <strong className="text-foreground">No Expiration:</strong> Purchased credits never expire and remain in your account for future use.
                </p>
                <p>
                  <strong className="text-foreground">No Refunds for Used Credits:</strong> Credits that have been used to grade assignments are non-refundable. See our Refund Policy for details on unused credits.
                </p>
                <p>
                  <strong className="text-foreground">Payment Processing:</strong> All payments are processed securely through Razorpay. We do not store your payment information.
                </p>
              </div>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">User Accounts</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Account Creation:</strong> You must create an account to use our Service. You may sign up using email or through Google OAuth authentication.
                </p>
                <p>
                  <strong className="text-foreground">Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                </p>
                <p>
                  <strong className="text-foreground">Accurate Information:</strong> You agree to provide accurate, current, and complete information during registration and to update such information as necessary.
                </p>
              </div>
            </section>

            {/* Acceptable Use */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Acceptable Use</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>You agree to use AssignOwl only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Upload content that infringes on intellectual property rights</li>
                  <li>Use the Service to violate any academic integrity policies</li>
                  <li>Attempt to gain unauthorized access to our systems or networks</li>
                  <li>Upload malicious code, viruses, or harmful content</li>
                  <li>Use automated systems (bots) to access the Service without permission</li>
                  <li>Resell or redistribute our Service without authorization</li>
                  <li>Reverse engineer or attempt to extract source code from our platform</li>
                </ul>
              </div>
            </section>

            {/* Academic Integrity */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Academic Integrity Notice</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  AssignOwl is designed as a tool to help students <strong className="text-foreground">review and improve their work</strong> before final submission. We strongly encourage users to:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Use our Service as a learning and self-assessment tool</li>
                  <li>Comply with your institution's academic integrity policies</li>
                  <li>Understand that AI feedback should be used as guidance, not as a substitute for your own work</li>
                  <li>Submit only your original work to your educational institution</li>
                </ul>
                <p className="text-yellow-600 dark:text-yellow-500 font-medium">
                  We are not responsible for any academic integrity violations resulting from misuse of our Service.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Intellectual Property</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Your Content:</strong> You retain all rights to the assignments and content you upload. By uploading content, you grant us a limited license to process, analyze, and score your assignments.
                </p>
                <p>
                  <strong className="text-foreground">Our Platform:</strong> The AssignOwl platform, including its design, features, algorithms, and branding, are owned by Greenguide Solutions Pvt Limited and protected by intellectual property laws.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Limitation of Liability</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  AssignOwl is provided "as is" without warranties of any kind. We do not guarantee:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>That AI-generated scores will match human grader assessments</li>
                  <li>Uninterrupted or error-free service</li>
                  <li>That the Service will meet your specific requirements</li>
                  <li>That defects will be corrected immediately</li>
                </ul>
                <p className="mt-4">
                  To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.
                </p>
              </div>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Termination</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  We reserve the right to suspend or terminate your account if you violate these Terms or engage in conduct that we deem harmful to our Service or other users. You may terminate your account at any time through your account settings.
                </p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Changes to Terms</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  We reserve the right to modify these Terms at any time. We will notify you of significant changes via email or through a notice on our platform. Your continued use after changes are posted constitutes acceptance of the modified Terms.
                </p>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Governing Law</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts in India.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="pt-6 border-t border-white/[0.1]">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Us</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>If you have questions about these Terms and Conditions, please contact us:</p>
                <p className="text-foreground">
                  <strong>Email:</strong> legal@assignowl.com<br />
                  <strong>Company:</strong> Greenguide Solutions Pvt Limited
                </p>
              </div>
            </section>

            <div className="text-sm text-muted-foreground text-center pt-6 border-t border-white/[0.1]">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
